import { MobXProviderContext } from 'mobx-react';
import { useConnectionCoordinator } from 'Modules/softphone/hooks';
import { useUIStore } from 'Modules/ui/index.store';
import React, { useEffect, useRef } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { getLogger } from 'Utils/logger';

const useShowConnectionBanner = () => {
  const {
    pusher: {
      isOnline,
      isPersonalChannelActuallySubscribed,
      isAccountChannelActuallySubscribed,
    },
    person: { IsLoggedIn },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  // Use coordination hook for connection health from active softphone
  const {
    isWebRTCConnected,
    isWebRTCReconnecting,
    webRTCRegistrationConcluded,
  } = useConnectionCoordinator();

  const [hasInternetConnection, setHasInternetConnection] =
    React.useState(true);

  const {
    showConnectionBanner,
    setShowConnectionBanner,
    toleranceTimeSurpassed,
    setToleranceTimeSurpassed,
  } = useUIStore();

  const connectionWasOff = useRef<boolean>(false);
  const hideBannerTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
  const lastFailureReasonsRef = useRef<string[] | null>(null);
  const showBannerTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(() => {
    const handleOnline = () => setHasInternetConnection(true);
    const handleOffline = () => setHasInternetConnection(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    !toleranceTimeSurpassed &&
      setTimeout(() => {
        setToleranceTimeSurpassed(true);
      }, 10000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toleranceTimeSurpassed, setToleranceTimeSurpassed]);

  useEffect(() => {
    if (!toleranceTimeSurpassed || !IsLoggedIn) {
      setShowConnectionBanner(false);
      return;
    }

    if (
      !isOnline ||
      !hasInternetConnection ||
      !isWebRTCConnected ||
      isWebRTCReconnecting ||
      !webRTCRegistrationConcluded ||
      !isPersonalChannelActuallySubscribed ||
      !isAccountChannelActuallySubscribed
    ) {
      if (hideBannerTimeout.current) {
        clearTimeout(hideBannerTimeout.current);
        hideBannerTimeout.current = undefined;
        // Preserve the recovery state to re-arm the dismiss timeout after a
        // connection interruption.
        connectionWasOff.current = true;
      }

      const failedChecks: string[] = [];
      if (!isOnline) failedChecks.push('pusherOffline');
      if (!hasInternetConnection) failedChecks.push('noInternetConnection');
      if (!isWebRTCConnected) failedChecks.push('webrtcDisconnected');
      if (isWebRTCReconnecting) failedChecks.push('webrtcReconnecting');
      if (!webRTCRegistrationConcluded)
        failedChecks.push('webRTCRegistrationConcluded');
      if (!isPersonalChannelActuallySubscribed)
        failedChecks.push('personalChannelUnsubscribed');
      if (!isAccountChannelActuallySubscribed)
        failedChecks.push('accountChannelUnsubscribed');

      // Debounce showing the offline banner for 5 seconds.
      // Only show if the disconnection persists; cancel if we recover in time.
      lastFailureReasonsRef.current = failedChecks;
      if (!showBannerTimeout.current) {
        showBannerTimeout.current = setTimeout(() => {
          const logPrefix = 'ConnectionBanner shown: offline';
          const logData = {
            failedChecks: lastFailureReasonsRef.current || failedChecks,
            state: {
              isOnline,
              hasInternetConnection,
              isWebRTCConnected,
              isWebRTCReconnecting,
              webRTCRegistrationConcluded,
              isPersonalChannelActuallySubscribed,
              isAccountChannelActuallySubscribed,
            },
          };
          // eslint-disable-next-line no-console
          console.log(logPrefix, logData);
          getLogger().warn(logPrefix, logData);

          setShowConnectionBanner('offline');
          connectionWasOff.current = true;
          showBannerTimeout.current = undefined;
        }, 5000);
      }

      return;
    }

    if (
      connectionWasOff.current &&
      isOnline &&
      hasInternetConnection &&
      isWebRTCConnected &&
      webRTCRegistrationConcluded &&
      !isWebRTCReconnecting &&
      isPersonalChannelActuallySubscribed &&
      isAccountChannelActuallySubscribed
    ) {
      if (showBannerTimeout.current) {
        clearTimeout(showBannerTimeout.current);
        showBannerTimeout.current = undefined;
      }
      const alreadyShowingOnline =
        useUIStore.getState().showConnectionBanner === 'online';

      // Only show the online banner and track analytics once per recovery cycle.
      if (!alreadyShowingOnline) {
        const recoveredFrom = lastFailureReasonsRef.current || [];
        const logPrefix = 'ConnectionBanner shown: online';
        const logData = {
          recoveredFrom,
          state: {
            isOnline,
            hasInternetConnection,
            isWebRTCConnected,
            isWebRTCReconnecting,
            webRTCRegistrationConcluded,
            isPersonalChannelActuallySubscribed,
            isAccountChannelActuallySubscribed,
          },
        };

        // eslint-disable-next-line no-console
        console.log(logPrefix, logData);
        getLogger().info('ConnectionBanner shown: online', {
          logPrefix,
          logData,
        });

        setShowConnectionBanner('online');
        pushToGTMDataLayer('connectivity_banner_restored');
      }

      connectionWasOff.current = false;
      lastFailureReasonsRef.current = null;

      hideBannerTimeout.current = setTimeout(() => {
        setShowConnectionBanner(false);
        // Clear the handle so it only stays set while a dismiss is pending.
        hideBannerTimeout.current = undefined;
      }, 3000);
    }

    // If everything is healthy and a show timeout is pending, cancel it.
    if (
      isOnline &&
      hasInternetConnection &&
      isWebRTCConnected &&
      !isWebRTCReconnecting &&
      webRTCRegistrationConcluded &&
      isPersonalChannelActuallySubscribed &&
      isAccountChannelActuallySubscribed
    ) {
      if (showBannerTimeout.current) {
        clearTimeout(showBannerTimeout.current);
        showBannerTimeout.current = undefined;
        lastFailureReasonsRef.current = null;
      }
    }
  }, [
    isOnline,
    hasInternetConnection,
    isWebRTCConnected,
    webRTCRegistrationConcluded,
    isWebRTCReconnecting,
    isPersonalChannelActuallySubscribed,
    isAccountChannelActuallySubscribed,
    toleranceTimeSurpassed,
    setShowConnectionBanner,
    IsLoggedIn,
  ]);

  useEffect(() => {
    return () => {
      if (hideBannerTimeout.current) {
        clearTimeout(hideBannerTimeout.current);
        hideBannerTimeout.current = undefined;
      }
      if (showBannerTimeout.current) {
        clearTimeout(showBannerTimeout.current);
        showBannerTimeout.current = undefined;
      }
    };
  }, []);

  return showConnectionBanner;
};

export default useShowConnectionBanner;
